import React, { useEffect, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { View, Image, TouchableOpacity, ScrollView } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShare,
  faHeart,
  faCalendar,
  faMapLocation,
  faTicket,
  faDollarSign,
  faHourglass,
  faStopwatch,
} from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'react-native-toast-notifications';
import { Page, BText, showToast, Drawer } from '../../components';
import { RegistrationTickets } from '../../components/events';
import { Button } from '../../components/Form';
import { formatTime24, formatFullDate } from '../../utils/dateUtils';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { EventModel, MyEventModel, MyFavoriteModel } from '../../types';
import { RegisteredTag, RegisteredSuccess } from '../../components/events';
import { setLoading } from '../../store/portal/portalActions';
import { fetchAllEvents } from '../../store/events/eventActions';
import { breakpointWidth } from '../../utils/helper';
import { themeConfig } from '../../styles/themeConfig';
import { validateLogon } from '../../utils/auth';
import { registerMyEvents, clearMyEventsRegStatus } from '../../store/myEvents/myEventActions';
import { addFavorite, removeFavorite } from '../../store/myFavorites/myFavoritesActions';
import attachmentService from '../../services/attachmentService';
import { LoginForm } from '../../components/Auth';
import { styles } from './styles';

export const Event = ({ navigation }: any) => {
  const toast = useToast();
  const route = useRoute();

  const dispatch = useAppDispatch();

  const [rtPortal, rtUser, rtEvents, rtMyEvents, rtMyFav] = useAppSelector(s => [
    s.portal,
    s.user,
    s.event,
    s.myEvent,
    s.myFavorites,
  ]);

  const { eventId } = route.params as { eventId: string };

  const [event, setEvent] = useState<EventModel>();
  const [registeredEvent, setRegisteredEvent] = useState<MyEventModel | null>(null);
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  const [isFav, setFav] = useState<boolean>(false);

  const [isLogon, setLogon] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);

  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [showRegistered, setRegistered] = useState<boolean>(false);
  const [showSuccess, setSuccess] = useState<boolean>(false);
  const [showTicket, setShowTicket] = useState<boolean>(false);

  useEffect(() => {
    // Sample for registration
    //let registrationInfo: RegistrationModel = sampleRegistration[0];
    //setRegistration(registrationInfo);
    //setRegistered(true);
    //setSuccess(true);
    //if (rtMyEvents.status) {

    // Reset Reg Status
    dispatch(clearMyEventsRegStatus());
    //}
  }, []);

  useEffect(() => {
    (async () => {
      if (await validateLogon(rtUser)) {
        setLogon(true);
        console.log('is login', true);
      }
    })();
  }, [rtUser.user]);

  useEffect(() => {
    const events: EventModel[] = rtEvents.allEvents;
    let curEvent: EventModel | undefined = events.find((a: EventModel) => a._id === eventId);

    if (!rtEvents.loaded || !curEvent) {
      console.log('event not loaded, fetch by ID');
      dispatch(setLoading(true, 'Event'));
      dispatch(
        fetchAllEvents({
          limit: 30,
          searchParam: {
            eventId: eventId,
          },
          countToGet: 5,
        }),
      );
    } else {
      dispatch(setLoading(false, 'Event'));
      setEvent(curEvent);

      getImage();
    }
  }, [rtEvents, eventId]);

  // to handle is the event is registered
  useEffect(() => {
    if (rtMyEvents.allEvents.length > 0 && eventId) {
      // check is registered
      let myEvent: MyEventModel | null | undefined = rtMyEvents.allEvents.find(
        (e: MyEventModel) => e.eventId === eventId,
      );

      setRegisteredEvent(myEvent || null);

      dispatch(setLoading(false, 'Event'));

      if (myEvent) {
        setRegistered(true);
      } else {
        setRegistered(false);
      }
    }
  }, [rtMyEvents, eventId]);

  useEffect(() => {
    if (rtMyFav.allEvents.length && eventId) {
      let favEvent: MyFavoriteModel | undefined = rtMyFav.allEvents.find(e => e.eventId === eventId);
      if (favEvent) {
        setFav(true);
      } else {
        setFav(false);
      }
    }
  }, [rtMyFav, eventId]);

  // to handle registration success & show success message
  useEffect(() => {
    setSuccess(rtMyEvents.status);
  }, [rtMyEvents.status]);

  const registerEvent = () => {
    setShowRegistration(true);
  };

  const confirmRegistration = () => {
    if (event) {
      dispatch(setLoading(true, 'Event - confirm reg'));
      dispatch(registerMyEvents(event));
    }
  };

  const viewEventTicket = () => {
    setShowTicket(true);
  };

  const getImage = async () => {
    const attachment = await attachmentService.getAttachment('event', eventId);
    setThumbnail(attachment.data.files[0].image);
  };

  const toggleFav = () => {
    if (isLogon) {
      if (event) {
        if (isFav) {
          dispatch(removeFavorite(event));
        } else {
          dispatch(addFavorite(event));
        }
      }
    } else {
      setShowLogin(true);
    }
  };

  return (
    <Page
      title={''}
      style={[styles.container, showRegistered ? styles.registeredBg : null]}
      contentStyle={[
        rtPortal.isSmallScreen
          ? { height: rtPortal.height - themeConfig.settings.mobileContentOffsetWithoutPageTitle, paddingBottom: 36 }
          : null,
        showRegistered ? styles.registeredBg : null,
      ]}
      useScrollView={!rtPortal.isSmallScreen}
    >
      {event && (
        <ScrollView>
          <View
            style={[
              styles.verticalFlex,
              rtPortal.isSmallScreen
                ? { width: '100%', flexDirection: 'column' }
                : { width: breakpointWidth(rtPortal.breakpoint) },
            ]}
          >
            <View style={[styles.eventDetails, !rtPortal.isSmallScreen ? { flexDirection: 'row' } : null]}>
              {thumbnail && (
                <View style={!rtPortal.isSmallScreen ? styles.thumbMDContainer : {}}>
                  <Image source={{ uri: thumbnail }} style={styles.thumbImg} />
                </View>
              )}

              <View style={[styles.detailMDContainer, !rtPortal.isSmallScreen ? styles.paddingHorizontal16 : null]}>
                {showRegistered && (
                  <View style={rtPortal.isSmallScreen ? styles.registerTagSM : styles.registerTagMD}>
                    <RegisteredTag />
                  </View>
                )}
                <BText style={[styles.title, !rtPortal.isSmallScreen ? { paddingTop: 0 } : null]}>
                  {event.eventName}
                </BText>
                <BText style={styles.desc}>{event.eventDescription}</BText>
                <TouchableOpacity
                  onPress={() => showToast(toast, { title: 'Implementation', message: 'For future use' })}
                >
                  <BText style={styles.viewMoreToggle}>View less</BText>
                </TouchableOpacity>
                <View style={styles.btnContainer}>
                  <Button
                    containerStyle={styles.btnItemContainer}
                    buttonStyle={styles.btnItem}
                    onPress={() => showToast(toast, { title: 'Implementation', message: 'For future use' })}
                  >
                    <FontAwesomeIcon icon={faShare} size="1x" />
                  </Button>
                  <Button containerStyle={styles.btnItemContainer} buttonStyle={styles.btnItem} onPress={toggleFav}>
                    <FontAwesomeIcon icon={faHeart} style={isFav ? styles.favIconSelected : styles.favIcon} size="1x" />
                  </Button>

                  {!rtPortal.isSmallScreen && (
                    <View style={styles.btnRegOrViewTicketContainerMD}>
                      {isLogon && !showRegistered && <Button onPress={registerEvent}>Register</Button>}
                      {isLogon && showRegistered && <Button onPress={viewEventTicket}>View Ticket</Button>}
                    </View>
                  )}
                </View>
              </View>
            </View>
            <View style={[styles.card, rtPortal.isSmallScreen ? styles.cardSM : styles.cardMD]}>
              <BText style={styles.cardTitle}>Task Details</BText>
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faCalendar} size="1x" />
                </View>
                <BText style={styles.cardItemText}>
                  {event.displayDate}
                  {'\n'}
                  {formatTime24(event.eventTime.start || '')} - {formatTime24(event.eventTime.end || '')}
                </BText>
              </View>
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faHourglass} size="1x" />
                </View>
                <BText style={styles.cardItemText}>Duration {event.eventDuration}</BText>
              </View>
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faStopwatch} size="1x" />
                </View>
                <BText style={styles.cardItemText}>
                  Register by {formatTime24(event.eventRegistration.end)}
                  {', '}
                  {formatFullDate(event.eventRegistration.end)}
                </BText>
              </View>

              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faMapLocation} size="1x" />
                </View>
                <BText style={styles.cardItemText}>
                  Hosted at {event.venueInformation.address} . {event.venueInformation.city}
                </BText>
              </View>
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faTicket} size="1x" />
                </View>
                <BText style={styles.cardItemText}>{event.numberOfTickets} tickets left</BText>
              </View>
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faDollarSign} size="1x" />
                </View>
                <BText style={styles.cardItemText}>RM {event.eventPrice}</BText>
              </View>
            </View>

            <View style={[styles.card, rtPortal.isSmallScreen ? styles.cardSM : styles.cardMD]}>
              <BText style={styles.cardTitle}>Speakers</BText>
              <View style={styles.cardSpeakersContainer}>
                {event.speakers?.map((speaker, idx) => {
                  return (
                    <View key={idx}>
                      <BText style={styles.cardItemText}>{speaker.name}</BText>
                      <BText style={styles.cardItemText}>{speaker.bio}</BText>
                    </View>
                  );
                })}
              </View>
            </View>

            <View style={[styles.card, rtPortal.isSmallScreen ? styles.cardSM : styles.cardMD]}>
              <BText style={styles.cardTitle}>Organizer</BText>
              <View style={styles.cardSpeakersContainer}>
                <BText style={styles.cardItemText}>{event.organizer.company}</BText>
                <BText style={styles.cardItemText}>Contact person: {event.organizer.contactPerson.name}</BText>
                {event.organizer.contactPerson.contactDetails.map((c, idx) => {
                  return (
                    <BText style={styles.cardItemText}>
                      {c.contactType} - {c.contact}
                    </BText>
                  );
                })}
              </View>
            </View>

            <View style={[styles.card, rtPortal.isSmallScreen ? styles.cardSM : styles.cardMD]}>
              <BText style={styles.cardTitle}>Fineprint</BText>
              <BText style={styles.cardItemText}>{event.finePrint}</BText>
            </View>

            <View style={[styles.card, rtPortal.isSmallScreen ? styles.cardSM : styles.cardMD]}>
              <BText style={styles.cardTitle}>Terms and conditions</BText>
              <BText style={styles.cardItemText}>{event.termsAndConditions}</BText>
            </View>

            <View style={[styles.card, rtPortal.isSmallScreen ? styles.cardSM : styles.cardMD]}>
              <BText style={styles.cardTitle}>Policy</BText>
              <BText style={styles.cardItemText}>{event.policy}</BText>
            </View>
          </View>
        </ScrollView>
      )}
      {rtPortal.isSmallScreen && (
        <View style={styles.btnRegOrViewTicketContainerSM}>
          {isLogon && !showRegistered && <Button onPress={registerEvent}>Register</Button>}
          {isLogon && showRegistered && <Button onPress={viewEventTicket}>View Ticket</Button>}
        </View>
      )}

      <Drawer
        id="event.registration"
        visible={showRegistration}
        type={'onCancel'}
        onClose={() => setShowRegistration(false)}
        onOK={confirmRegistration}
        title={'Confirmation'}
      >
        <BText>Are you confirm the booking?</BText>
      </Drawer>

      <Drawer id="event.registered" visible={showSuccess} type={'custom'} onClose={() => setSuccess(false)}>
        {registeredEvent && <RegisteredSuccess event={registeredEvent} onClose={() => setSuccess(false)} />}
      </Drawer>

      <Drawer id="event.ticket" visible={showTicket} onClose={() => setShowTicket(false)} type="custom">
        {registeredEvent && <RegistrationTickets event={registeredEvent} onClose={() => setShowTicket(false)} />}
      </Drawer>

      <Drawer id="event.login" visible={showLogin} type="custom" onClose={() => setShowLogin(false)}>
        <LoginForm onClose={() => setShowLogin(false)} navigation={navigation} />
      </Drawer>
    </Page>
  );
};
